import { permissionActionRegistry } from '@/features/permissions/permissionActions.js'
import { reportPermissionMisconfiguration } from '@/features/permissions/permissionDiagnostics.js'
import {
  getSuperAdminLimitedRoleEvidence,
  getTenantRoleEvidence,
  normalizeActorFromMyDetails,
  normalizeTenantForPermission,
} from '@/features/permissions/normalizeActorFromMyDetails.js'

function makeDecision({ allowed, reason, actor, action, context, evidence = {}, warnings = [] }) {
  return {
    allowed,
    reason,
    actor,
    action,
    actionDefinition: permissionActionRegistry[action] ?? null,
    mode: context.superAdminMode,
    surface: context.surface,
    route: context.route,
    tenantId: context.activeTenant?.tenantId ?? context.activeTenantId,
    tenantintId: context.activeTenant?.tenantintId,
    evidence,
    warnings,
  }
}

function resolveTenant(actor, activeTenantId) {
  if (!activeTenantId) return null
  return actor.tenants.find(tenant => tenant.tenantId === activeTenantId) ?? null
}

export function decidePermission(action, inputContext = {}) {
  const actor = normalizeActorFromMyDetails(inputContext.myDetails)
  const actionDefinition = permissionActionRegistry[action]
  const context = {
    superAdminMode: actor.isFullSuperAdmin
      ? 'full-access'
      : actor.isSuperAdminLimited && inputContext.surface === 'super-admin'
        ? 'super-admin-limited'
        : 'tenant-scoped',
    surface: inputContext.surface || 'admin',
    route: inputContext.route,
    activeTenantId: inputContext.activeTenantId,
    activeTenant: inputContext.activeTenant,
  }

  if (!actionDefinition) {
    reportPermissionMisconfiguration('unknown_action', {
      action,
      route: context.route,
      surface: context.surface,
    })

    return makeDecision({
      allowed: false,
      reason: 'unknown_action',
      actor,
      action,
      context,
    })
  }

  if (actor.isFullSuperAdmin) {
    return makeDecision({
      allowed: true,
      reason: 'super_admin_full_access',
      actor,
      action,
      context,
      evidence: {
        endpoints: actionDefinition.endpoints ?? [],
      },
    })
  }

  if (context.surface === 'super-admin') {
    if (!actor.isSuperAdminLimited) {
      return makeDecision({
        allowed: false,
        reason: 'not_super_admin_limited',
        actor,
        action,
        context,
      })
    }

    const evidence = getSuperAdminLimitedRoleEvidence(actor)
    const roleNames = new Set(evidence.roleNames)

    if (roleNames.has(action)) {
      return makeDecision({
        allowed: true,
        reason: 'super_admin_limited_role_name_allowed',
        actor,
        action,
        context,
        evidence: {
          ...evidence,
          endpoints: actionDefinition.endpoints ?? [],
        },
      })
    }

    return makeDecision({
      allowed: false,
      reason: 'missing_super_admin_limited_role_name',
      actor,
      action,
      context,
      evidence: {
        ...evidence,
        endpoints: actionDefinition.endpoints ?? [],
      },
    })
  }

  const tenant =
    resolveTenant(actor, context.activeTenantId) ??
    normalizeTenantForPermission(context.activeTenant)

  if (!tenant) {
    if (actor.isSuperAdminLimited && context.activeTenantId) {
      const evidence = getSuperAdminLimitedRoleEvidence(actor)
      const roleNames = new Set(evidence.roleNames)

      if (roleNames.has(action)) {
        return makeDecision({
          allowed: true,
          reason: 'super_admin_limited_role_name_allowed_for_tenant_surface',
          actor,
          action,
          context,
          evidence: {
            ...evidence,
            endpoints: actionDefinition.endpoints ?? [],
          },
        })
      }
    }

    return makeDecision({
      allowed: false,
      reason: 'missing_tenant_context',
      actor,
      action,
      context,
    })
  }

  const evidence = getTenantRoleEvidence(tenant)
  const roleNames = new Set(evidence.roleNames)

  if (roleNames.has(action)) {
    return makeDecision({
      allowed: true,
      reason: 'role_name_allowed',
      actor,
      action,
      context: {
        ...context,
        activeTenant: tenant,
      },
      evidence: {
        ...evidence,
        endpoints: actionDefinition.endpoints ?? [],
      },
    })
  }

  if (actor.isSuperAdminLimited) {
    const superAdminLimitedEvidence = getSuperAdminLimitedRoleEvidence(actor)
    const superAdminLimitedRoleNames = new Set(superAdminLimitedEvidence.roleNames)

    if (superAdminLimitedRoleNames.has(action)) {
      return makeDecision({
        allowed: true,
        reason: 'super_admin_limited_role_name_allowed_for_tenant_surface',
        actor,
        action,
        context: {
          ...context,
          activeTenant: tenant,
        },
        evidence: {
          ...superAdminLimitedEvidence,
          endpoints: actionDefinition.endpoints ?? [],
        },
      })
    }
  }

  return makeDecision({
    allowed: false,
    reason: 'missing_role_name',
    actor,
    action,
    context: {
      ...context,
      activeTenant: tenant,
    },
    evidence: {
      ...evidence,
      endpoints: actionDefinition.endpoints ?? [],
    },
  })
}
